import { useState } from 'react';
import {
  signIn,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';

/**
 * Email/password sign-in for dev (no hosted-UI domain needed). Handles:
 *  - the first-login NEW_PASSWORD_REQUIRED challenge (admin-created users set a new password);
 *  - self-service forgot-password (Cognito emails a code, the user sets a new password), using
 *    Amplify resetPassword / confirmResetPassword. Cognito is the source of truth; no backend.
 */
type Mode = 'signIn' | 'newPassword' | 'forgotRequest' | 'forgotConfirm';

export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Which form is showing: sign-in, first-login new password, or the two forgot-password steps.
  const [mode, setMode] = useState<Mode>('signIn');
  const needNewPassword = mode === 'newPassword';

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { nextStep } = await signIn({ username: email, password });
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // First login with a temporary password — prompt for a new one before completing.
        setMode('newPassword');
        return;
      }
      if (nextStep.signInStep === 'DONE') {
        onSignedIn();
        return;
      }
      // Any other step (MFA etc.) isn't supported by this dev sign-in yet.
      setError(`Additional sign-in step required: ${nextStep.signInStep}`);
    } catch (err) {
      setError((err as Error).message ?? 'Sign in failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { nextStep } = await confirmSignIn({ challengeResponse: newPassword });
      if (nextStep.signInStep === 'DONE') {
        onSignedIn();
        return;
      }
      setError(`Additional sign-in step required: ${nextStep.signInStep}`);
    } catch (err) {
      setError((err as Error).message ?? 'Could not set the new password');
    } finally {
      setBusy(false);
    }
  };

  // Forgot-password step 1: request a reset code to the user's email.
  const handleForgotRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await resetPassword({ username: email });
      setNotice('We\u2019ve emailed you a reset code. Enter it below with your new password.');
      setMode('forgotConfirm');
    } catch (err) {
      setError((err as Error).message ?? 'Could not start the password reset');
    } finally {
      setBusy(false);
    }
  };

  // Forgot-password step 2: confirm the emailed code and set a new password.
  const handleForgotConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: resetCode.trim(),
        newPassword,
      });
      // Back to sign-in with the new password.
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setResetCode('');
      setMode('signIn');
      setNotice('Your password has been reset. Please sign in with your new password.');
    } catch (err) {
      setError((err as Error).message ?? 'Could not reset the password');
    } finally {
      setBusy(false);
    }
  };

  // Switch modes, clearing transient messages/fields so forms don't carry stale state.
  const goToMode = (m: Mode) => {
    setError(null);
    setNotice(null);
    setMode(m);
  };

  const subtitle = {
    signIn: 'Sign in',
    newPassword: 'Set a new password',
    forgotRequest: 'Reset your password',
    forgotConfirm: 'Enter your reset code',
  }[mode];

  return (
    <Container className="d-flex align-items-center justify-content-center min-vh-100">
      <Card className="shadow-sm" style={{ width: '100%', maxWidth: 380 }}>
        <Card.Body>
          <Card.Title as="h1" className="h4 mb-1">Telemedicine Platform</Card.Title>
          <Card.Subtitle className="text-muted mb-3">{subtitle}</Card.Subtitle>
          {error && <Alert variant="danger">{error}</Alert>}
          {notice && <Alert variant="success">{notice}</Alert>}

          {mode === 'signIn' && (
            <Form onSubmit={handleSignIn}>
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="username"
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="password">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </Form.Group>
              <Button type="submit" disabled={busy} className="w-100">
                {busy ? <><Spinner animation="border" size="sm" className="me-2" />Signing in…</> : 'Sign in'}
              </Button>
              <div className="text-center mt-3">
                <Button variant="link" className="p-0" onClick={() => goToMode('forgotRequest')}>
                  Forgot password?
                </Button>
              </div>
            </Form>
          )}

          {needNewPassword && (
            <Form onSubmit={handleSetNewPassword}>
              <p className="text-muted small">
                This is your first sign-in. Please choose a new password.
              </p>
              <Form.Group className="mb-3" controlId="newPassword">
                <Form.Label>New password</Form.Label>
                <Form.Control
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="confirmPassword">
                <Form.Label>Confirm new password</Form.Label>
                <Form.Control
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </Form.Group>
              <Button type="submit" disabled={busy} className="w-100">
                {busy ? <><Spinner animation="border" size="sm" className="me-2" />Saving…</> : 'Set password & sign in'}
              </Button>
            </Form>
          )}

          {mode === 'forgotRequest' && (
            <Form onSubmit={handleForgotRequest}>
              <p className="text-muted small">
                Enter your email and we&rsquo;ll send a reset code.
              </p>
              <Form.Group className="mb-3" controlId="forgotEmail">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="username"
                />
              </Form.Group>
              <Button type="submit" disabled={busy} className="w-100">
                {busy ? <><Spinner animation="border" size="sm" className="me-2" />Sending…</> : 'Send reset code'}
              </Button>
              <div className="text-center mt-3">
                <Button variant="link" className="p-0" onClick={() => goToMode('signIn')}>
                  Back to sign in
                </Button>
              </div>
            </Form>
          )}

          {mode === 'forgotConfirm' && (
            <Form onSubmit={handleForgotConfirm}>
              <p className="text-muted small">
                Enter the code we emailed to <strong>{email}</strong> and choose a new password.
              </p>
              <Form.Group className="mb-3" controlId="resetCode">
                <Form.Label>Reset code</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  required
                  autoComplete="one-time-code"
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="forgotNewPassword">
                <Form.Label>New password</Form.Label>
                <Form.Control
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="forgotConfirmPassword">
                <Form.Label>Confirm new password</Form.Label>
                <Form.Control
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </Form.Group>
              <Button type="submit" disabled={busy} className="w-100">
                {busy ? <><Spinner animation="border" size="sm" className="me-2" />Resetting…</> : 'Reset password'}
              </Button>
              <div className="text-center mt-3">
                <Button variant="link" className="p-0" onClick={() => goToMode('signIn')}>
                  Back to sign in
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
